import { Container, Graphics, Text } from '@pixi/react'
import { TextStyle, Container as PixiContainer } from 'pixi.js'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import gsap from 'gsap'
import { useAutoPlayStore } from '../../domain/autoPlay/store/autoPlayStore'
import { appScreen } from '../../util/pixi-app'
import { Draw } from '../../type/PixiType'
import { AutoPlayOptionV2 } from './AutoPlayOptionV2'
import { autoPlayOptionsV2 } from '../../domain/autoPlay'
import { soundManager, SoundType } from '../../domain/sound/SoundManager'
import { Button } from '../../UI/Button'
import { AutoConfirmButton } from './AutoConfirmButton'
import { t } from '../../domain/language/languageUtil'

const slide = (container: PixiContainer, targetY: number) => {
  gsap.to(container as PixiContainer, {
    duration: 0.2,
    y: targetY,
  })
}
interface AutoPanelProps {
  onConfirm: () => void
  borderColor?: string
}

export const AutoPanel = (props: AutoPanelProps) => {
  const { borderColor = '#4775AF' } = props
  const popUpRef = useRef<PixiContainer>()

  const [current, isEnabledAutoPlayPanel] = useAutoPlayStore(x => [
    x.current,
    x.isEnabledPanel,
  ])

  useEffect(() => {
    if (!popUpRef.current) return
    if (isEnabledAutoPlayPanel) {
      slide(popUpRef.current, appScreen.height - 515)
    }
  }, [isEnabledAutoPlayPanel])

  const draw = useCallback<Draw>((graphics) => {
    graphics.clear()
    graphics.beginFill('#000000', 0.6)
    graphics.drawRect(0, -250, appScreen.width, appScreen.height + 250)
    graphics.endFill()
  }, [])

  const backgroundDraw = useCallback<Draw>((background) => {
    background.clear()
    background.beginFill('#000000')
    background.drawRoundedRect(0, 0, appScreen.width, 515, 0)
  }, [])

  const borderDraw = useCallback<Draw>((border) => {
    border.clear()
    border.lineStyle(2, borderColor, 5)
    border.drawRoundedRect(0, 0, appScreen.width, 515, 0)
  }, [borderColor])

  const optionDraw = useCallback<Draw>((background) => {
    background.clear()
    background.beginFill('#292929')
    background.drawRoundedRect(2, 0, appScreen.width - 4, 161, 0)
  }, [])

  const AutoPlayOptions = useMemo(() => {
    return autoPlayOptionsV2.map((option, index) => (
      <AutoPlayOptionV2
        x={-130 + index * 200}
        y={100}
        key={index}
        isHighlighted={current === option}
        radius={10}
        text={option}
      >
      </AutoPlayOptionV2>
    ))
  }, [current])

  const handleClose = useCallback(() => {
    soundManager.play(SoundType.buttonClose)
    if (popUpRef.current) {
      useAutoPlayStore.setState({
        isEnabledPanel: false,
      })
    }
  }, [])

  return isEnabledAutoPlayPanel
    ? (
        <>
          <Container name="auto pop up bg" eventMode="static">
            <Graphics draw={draw} />
          </Container>
          <Container
            name="auto pop up"
            x={0}
            y={appScreen.height}
            ref={(el: PixiContainer | null) => {
              popUpRef.current = el as PixiContainer
            }}
          >
            <Graphics draw={backgroundDraw} />
            <Graphics draw={borderDraw} />
            <Text
              text={t('auto_panel_title')}
              x={appScreen.width / 2}
              y={20}
              anchor={[0.5, 0]}
              style={new TextStyle({
                fontSize: 53,
                fontWeight: 'bold',
                fontFamily: 'Arial',
                fill: '#F0E1D4',
                align: 'center',
              })}
            />
            <Button
              name="close auto"
              enabled={true}
              defaultView="close_btn_normal"
              hoverView="close_btn_hover"
              pressedView="close_btn_click"
              x={appScreen.width - 80}
              y={10}
              width={69}
              height={69}
              onPress={handleClose}
            />
            <Text
              text={t('auto_panel_subtitle')}
              x={appScreen.width / 2}
              y={90}
              anchor={[0.5, 0]}
              style={
                new TextStyle({
                  fontSize: 26,
                  fill: '#8E847B',
                  align: 'center',
                })
              }
            />
            <Container
              x={0}
              y={140}
              name="options wrapper"
            >
              <Graphics draw={optionDraw} />
              <Container
                x={appScreen.width / 2 - 350}
                y={-55}
                anchor={[0.5, 0.5]}
                name="options"
              >
                {AutoPlayOptions}
              </Container>

            </Container>
            <AutoConfirmButton
              enabled={current !== 0}
              x={appScreen.width / 2 - 300}
              y={380}
              text={t('auto_panel_start_spin')}
              onClick={props.onConfirm}
            />
          </Container>
        </>
      )
    : null
}
